import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { listProjects, deleteProject, type Project, type RoleSession } from '../api/projects';
import { AppLayout } from '../components/AppLayout';
import { EmptyState, LoadingDots } from '../components/ChildFriendly';
import { ConfirmModal } from '../components/ConfirmModal';

export default function DashboardPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingDelete, setPendingDelete] = useState<Project | null>(null);
  const [deleting, setDeleting] = useState(false);

  if (user?.role === 'teacher') {
    return <Navigate to="/teacher" replace />;
  }

  useEffect(() => {
    let cancelled = false;
    listProjects()
      .then((d) => { if (!cancelled) setProjects(d); })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.detail || 'Projeler yüklenemedi.');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteProject(pendingDelete.id);
      setProjects((prev) => prev.filter((x) => x.id !== pendingDelete.id));
      setPendingDelete(null);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || 'Proje silinemedi.');
    } finally {
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <AppLayout title="Projelerim">
        <LoadingDots message="Projeler yükleniyor" />
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title="Projelerim"
      subtitle="Bir problem seç, yapay zekâyla rol rol konuşarak çözümünü savun."
      actions={
        <Link to="/projects/new" className="btn btn--primary btn--md">
          <span aria-hidden="true">+</span> Yeni Proje
        </Link>
      }
    >
      {error && <div role="alert" className="error">{error}</div>}

      {projects.length === 0 ? (
        <EmptyState
          icon="📝"
          title="Henüz hiç projen yok"
          message="Bir problem bulup çözümünü hazırla. Mezuniyet projen burada başlıyor!"
          action={
            <Link to="/projects/new" className="btn btn--primary btn--md">
              <span aria-hidden="true">✨</span> İlk Projeni Oluştur
            </Link>
          }
        />
      ) : (
        <ul className="project-list">
          {projects.map((p) => {
            const sessions = p.role_sessions ?? [];
            const passed = sessions.filter((r) => r.status === 'passed').length;
            const activeRole = pickActiveRole(sessions);
            const linkTo = activeRole
              ? `/projects/${p.id}/roles/${activeRole.id}`
              : `/projects/${p.id}`;

            return (
              <li key={p.id}>
                <Link
                  to={linkTo}
                  className={`project-card status-${p.status}`}
                  aria-label={`${p.problem_text} — ${statusLabel(p.status)}`}
                >
                  <div className="project-card__problem">{p.problem_text}</div>
                  <div className="project-card__meta">
                    <span className={`status-badge status-${p.status}`}>
                      {statusLabel(p.status)}
                    </span>
                    <span>
                      {sessions.length === 0
                        ? 'Rol atanmadı'
                        : `${passed} / ${sessions.length} rol`}
                    </span>
                  </div>
                </Link>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm project-card__delete"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setPendingDelete(p);
                  }}
                  aria-label={`Projeyi kalıcı olarak sil: ${p.problem_text.slice(0, 40)}`}
                  title="Projeyi kalıcı olarak sil (geri alınamaz)"
                >
                  🗑 Sil
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmModal
        open={pendingDelete !== null}
        title="Projeyi kalıcı olarak silmek istediğine emin misin?"
        message="Bu proje ve TÜM sohbet geçmişi, kanıt dosyaları ve varsa diploman silinecek."
        highlight={
          pendingDelete
            ? `📝 ${pendingDelete.problem_text.slice(0, 80)}${pendingDelete.problem_text.length > 80 ? '…' : ''}`
            : undefined
        }
        warning="Bu işlem geri alınamaz!"
        confirmLabel="Evet, kalıcı olarak sil"
        cancelLabel="Vazgeç"
        tone="danger"
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={() => !deleting && setPendingDelete(null)}
      />
    </AppLayout>
  );
}

// Aktif rol varsa oraya, yoksa bekleyen rol varsa oraya, yoksa ilk role
// (tümü geçmiş/kalmış) yönlendir — en doğal "kaldığın yerden devam et" sezgisi.
function pickActiveRole(sessions: RoleSession[]): RoleSession | undefined {
  const activeOrPending =
    sessions.find((r) => r.status === 'active') ??
    sessions.find((r) => r.status === 'pending');
  if (activeOrPending) return activeOrPending;
  const allFinished =
    sessions.length > 0 &&
    sessions.every((r) => r.status === 'passed' || r.status === 'failed');
  if (allFinished) return undefined;
  return sessions[0];
}


function statusLabel(status: Project['status']): string {
  switch (status) {
    case 'draft': return 'Taslak';
    case 'in_progress': return 'Devam ediyor';
    case 'passed': return 'Onaylandı';
    case 'failed': return 'Reddedildi';
    default: return status;
  }
}
